import { useState } from "react";
import type { ConversationMessage as Message } from "@edu/shared";
import { ApiError } from "../../lib/api.ts";
import { agoLabel } from "../../lib/when.ts";
import { TrashIcon } from "../shell/icons.tsx";
import {
  announcementErrorText,
  bodyLines,
  messageText,
  receiptText,
  removeMessageLabel,
} from "./announcement-text.ts";
import { deleteAnnouncement, deleteAnnouncementReply } from "./announcements-api.ts";

/**
 * Одно сообщение в окне переписки.
 *
 * Пять правил, каждое видно глазами на экране:
 *
 * 1. ⚠️ **Имя автора приезжает готовым полем.** Оно собрано сервером ПО
 *    ЧИТАТЕЛЮ (`authorNameFor`), и своей ветки выбора имени здесь нет ни одной.
 *    Что именно приедет: имя внутри группы, а если преподаватель его не задал,
 *    то имя из его списка учеников. У ВЫШЕДШЕГО из группы приезжает нейтральное
 *    «Ученик»: строки участия у него нет, значит преподаватель уже не может
 *    задать ему имя, и подставить полное было бы раскрытием, которому он не в
 *    силах помешать
 * 2. ⚠️ **Кому какое сообщение видно, решил сервер.** Ответ преподавателя своему
 *    адресату виден всегда, чужие ответы учеников только при включённом
 *    `repliesVisible`. Экран не фильтрует окно ничем: пришло, значит рисуем
 * 3. ⚠️ **Своё сообщение подписано «Вы», а не именем.** Не из экономии: подпись
 *    нужна читалке, которой выравнивание вправо не видно, а собственное имя
 *    человеку под его же словами не говорит ничего
 * 4. **Состояний у сообщения три** (есть текст, стёрт по сроку, удалён), и
 *    различает их `messageText`, а не разбор пары полей на месте
 * 5. ⚠️ **Квитанцию видит только отправитель**, и приезжает она числами, а не
 *    именами: кто именно открыл, остаётся знанием, которого в этом ответе нет
 *    вовсе
 */
export function ConversationMessageItem(props: {
  msg: Message;
  /** Сторона ЧИТАТЕЛЯ, приехавшая с сервера. Преподаватель убирает любое слово. */
  teacher: boolean;
  now: number;
  /**
   * Сообщение убрали. Само сообщение уезжает наверх, потому что окно поступает с
   * ответом и с объявлением по-разному: разбор у `onRemoved` в
   * `ConversationPage.tsx`.
   */
  onRemoved: (removed: Message) => void;
}) {
  const { msg } = props;
  const shown = messageText(msg);
  const when = agoLabel(msg.createdAt, props.now);
  const receipt =
    msg.recipientCount === undefined ? null : receiptText(msg.recipientCount, msg.seenCount ?? 0);

  /**
   * ⚠️ Убрать можно своё, а преподавателю любое, и это решение сервера, а не
   * разбор ролей здесь: предикат удаления написан ровно так же. Удалённое
   * второй раз не удаляется, поэтому у надгробия кнопки нет.
   */
  const removable =
    shown.state !== "deleted" && (props.teacher || (msg.mine && msg.kind === "reply"));

  /**
   * Вопрос «точно убрать?» задан.
   *
   * ⚠️ **Состояние живёт здесь, а не в `ConfirmRemove`, потому что удаление
   * теперь занимает два места пузыря**: значок стоит в шапке, а вопрос со
   * словами внизу, под текстом и квитанцией. Одному компоненту в две точки
   * разметки не отрисоваться, и общее состояние дешевле портала.
   *
   * Отказ прошлой попытки живёт внутри `ConfirmRemove` и уезжает вместе с ним:
   * человек передумал, значит и красная строка про неудачу больше не про него.
   */
  const [asking, setAsking] = useState(false);

  return (
    <li className="msg" data-mine={msg.mine} data-dim={shown.dim}>
      <div className="msg__head">
        <span className="msg__who">{msg.mine ? "Вы" : msg.authorName}</span>
        <span className="msg__when" title={when.title}>
          {when.text}
        </span>
        {/*
          Значок «Убрать» в правом верхнем углу пузыря (решение владельца
          30.08.2026).

          ⚠️ **Он виден всегда, а не по наведению.** Наведения нет ни на
          телефоне, ни на планшете, то есть спрятанный за ним значок там не
          достать ничем: действие бы просто исчезло у половины читателей. От
          лишнего веса на экране спасает не пряталка, а вид самой кнопки:
          тихий вес не рисует коробки вовсе, от кнопки остаётся одна линия
          значка, а необратимость называет её цвет.

          ⚠️ Пока задан вопрос, значка нет: два входа в одно и то же действие на
          одном пузыре читаются как два разных действия.
        */}
        {removable && !asking && (
          <button
            type="button"
            className="btn btn--ico btn--danger msg__del"
            title={removeMessageLabel(msg.kind)}
            aria-label={removeMessageLabel(msg.kind)}
            onClick={() => setAsking(true)}
          >
            <TrashIcon />
          </button>
        )}
      </div>

      {shown.state === "posted" ? (
        bodyLines(shown.text).map((line, i) => (
          // Ключ по номеру строки: это статичный текст, он не переставляется.
          // Пустая строка это неразрывный пробел: обычный в HTML схлопнется, и
          // абзацная разрядка пропала бы
          <p className="msg__line" key={i}>
            {line === "" ? " " : line}
          </p>
        ))
      ) : (
        <p className="msg__line muted">{shown.text}</p>
      )}

      {receipt !== null && <p className="msg__note">{receipt}</p>}

      {removable && asking && (
        <ConfirmRemove
          msg={msg}
          onCancel={() => setAsking(false)}
          onDone={() => {
            setAsking(false);
            props.onRemoved(msg);
          }}
        />
      )}
    </li>
  );
}

/**
 * Второй шаг удаления: вопрос словами и сам вызов ручки.
 *
 * Двумя нажатиями и красным на втором: отменить это нельзя, а вес кнопки
 * сообщает о необратимости заливкой (`is-confirm`), как у отзыва ссылки.
 * ⚠️ Приём ровно тот же, что у карточки занятия и у корзины домашки: значок
 * предлагает, слова с заливкой подтверждают. Окна поверх экрана здесь нет и не
 * заводится, третьего приёма на один и тот же вопрос в продукте быть не должно.
 *
 * ⚠️ **Ручки ДВЕ, и последствия у них разные.** Ответ убирается один
 * (`DELETE /v1/announcements/:id/replies/:replyId`), а объявление уносит с собой
 * всю переписку под ним у всех, кому оно пришло. Второе поэтому называется
 * словами до нажатия, а не после: и в подписи значка, и здесь.
 *
 * ⚠️ Удаление не выключается у отвязанного ученика, и это решение сервера, а не
 * пропуск экрана: писать он перестаёт сразу, а снять свои слова с глаз
 * остальных детей может всегда.
 */
function ConfirmRemove(props: { msg: Message; onCancel: () => void; onDone: () => void }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const post = props.msg.kind === "post";

  const run = () => {
    void (async () => {
      setBusy(true);
      try {
        if (post) await deleteAnnouncement(props.msg.id);
        else await deleteAnnouncementReply(props.msg.postId ?? "", props.msg.id);
        props.onDone();
      } catch (e) {
        // Словами из общего словаря переписки, а не сырым `message`: чаще всего
        // это «сообщения больше нет», и лечится оно перезагрузкой, а не повтором
        setErr(
          e instanceof ApiError
            ? announcementErrorText(e.code, e.message)
            : e instanceof Error
              ? e.message
              : String(e),
        );
      } finally {
        setBusy(false);
      }
    })();
  };

  return (
    <>
      {err !== null && <p className="err">{err}</p>}
      {post && (
        <p className="msg__note">Сообщение и ответы на него исчезнут у всех, кому оно пришло.</p>
      )}
      <div className="msg__actions">
        <button type="button" className="btn btn--danger is-confirm" disabled={busy} onClick={run}>
          Да, удалить
        </button>
        <button type="button" className="btn btn--quiet" disabled={busy} onClick={props.onCancel}>
          Отмена
        </button>
      </div>
    </>
  );
}
